/**
 * @destription 静态词云
 * @author duanjun
 */

import Sharp from './sharp';
import Tag from './tag';
import { dpr, PI } from './util';

export interface IWord {
    fontSize: number;
    color: string;
    text: string;
    fontWeight: string | number;
}

export default class Cloud {
    private target: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private tags: Tag[] = [];
    private container: HTMLDivElement;
    private words: IWord[] = [];
    private sharp!: Sharp;
    constructor(words: IWord[], container: HTMLDivElement) {
        this.container = container;
        this.words = words;
        this.target = document.createElement('canvas');
        this.context = this.target.getContext('2d') as CanvasRenderingContext2D;
        const width = Math.max(this.container.clientWidth, 400);
        this.resize(width);
        this.render();
    }

    public resize(width: number) {
        const ratio = 9 / 16;
        const capacity = 0.62;
        this.tags = [];
        let wordsArea = 0;
        // const length = this.words.length;
        // const padding = Math.round(width / (10 * length));
        this.words.forEach((word) => {
            // const tag = new Tag(Object.assign({ padding }, word));
            const tag = new Tag(word);
            this.tags.push(tag);
            wordsArea += tag.area();
        });

        if (Sharp.area(dpr * width / 2, width * ratio * dpr / 2) * capacity < wordsArea) {
            const axis = Sharp.calcuAxis(wordsArea / capacity, ratio);
            // 计算出来的是半径
            width = axis.a * 2 / dpr;
        }

        this.sharp = new Sharp({
            width: Math.round(width * dpr),
            height: Math.round(width * ratio * dpr),
        });
        this.target.width = this.sharp.width;
        this.target.height = this.sharp.height;
    }

    public clear() {
        this.context.clearRect(0, 0, this.target.width, this.target.height);
    }

    public mount() {
        const container = this.container;
        if (container) {
            const child = container.querySelector('canvas');
            if (child) {
                container.replaceChild(this.target, child);
            } else {
                container.appendChild(this.target);
            }
        }
    }

    public sort(tags: Tag[]) {
        tags.sort((tag1, tag2) => {
            if (tag1.fontSize > tag2.fontSize) {
                return -1;
            }
            if (tag1.fontSize < tag2.fontSize) {
                return 1;
            }
            return 0;
        });
    }

    public getTagsStatInfo(tags: Tag[]) {
        let width = 0;
        let height = 0;
        let minHeight = Infinity;
        let minWidth = Infinity;
        let maxWidth = 0;
        let maxHeight = 0;
        const length = tags.length;
        tags.forEach((tag) => {
            width += tag.width;
            height += tag.height;
            minHeight = Math.min(minHeight, tag.height);
            minWidth = Math.min(minWidth, tag.width);
            maxHeight = Math.max(maxHeight, tag.height);
            maxWidth = Math.max(maxWidth, tag.width);
        });
        return {
            averageHeight: height / length,
            averageWidth: width / length,
            maxWidth,
            maxHeight,
            minWidth,
            minHeight,
        };
    }

    public drawTag(tag: Tag) {
        this.context.drawImage(tag.canvas, tag.x - (tag.width / 2), tag.y - (tag.height / 2));
    }

    public insert(tag: Tag, x: number, y: number) {
        let insertEnable = true;
        tag.updatePosition(x, y);
        const points = tag.getVertexPoints()
            .concat(tag.getTexturePoints())
            .concat({ x, y });
        insertEnable = points.every((point) => {
            return !this.sharp.isPointFilled(point.x, point.y);
        });

        if (insertEnable) {
            const leftTopPoint = points[0];
            this.drawTag(tag);
            this.sharp.clearRect(leftTopPoint.x, leftTopPoint.y, tag.width, tag.height);
        } else {
            insertEnable = false;
        }
        return insertEnable;
    }

    public destroy() {
        this.tags = [];
    }

    public fill(tags: Tag[], stepB?: number, stepRad?: number) {
        const left = tags.length;
        if (!left) {
            return;
        }
        const tagsStatInfo = this.getTagsStatInfo(tags);
        stepRad = stepRad || Math.atan(tagsStatInfo.minHeight / tagsStatInfo.maxWidth);
        stepB = stepB || tagsStatInfo.minHeight;
        let tag = tags.shift();
        let a = 0;
        let b = stepB;
        let rad = 0;
        const circleRad = 2 * PI;
        // 保证插入均匀 , 圆弧上的对称点交换着插入
        let lines = 4;

        const tryInsert = (line: number) => {
            let point;
            point = this.sharp.getFillPoint(a, b, rad + line * ((2 * Math.PI) / lines));
            if (tag && this.insert(tag, point.x, point.y)) {
                tag = undefined;
                if (tags.length) {
                    tag = tags.shift();
                } else {
                    return;
                }
            }
        };

        for (; b < this.sharp.height / 2; b += stepB) {
            a = (this.sharp.width / this.sharp.height) * b;
            for (rad = 0; rad <= circleRad; rad += stepRad) {
                lines = b < this.sharp.height * 1 / 2 ? 1 : 4;
                for (let line = 0; line < lines; line++) {
                    tryInsert(lines);
                }
            }
            const newCircleRad = circleRad + (PI) * Math.round(1 + Math.random());
            rad = newCircleRad - 2 * PI;
        }
        if (tag) {
            tags.unshift(tag);
        }
        const leftAfterFill = tags.length;
        if (leftAfterFill < left) {
            this.fill(tags, stepB, stepRad);
        }
    }

    public render() {
        this.sort(this.tags);
        this.fill(this.tags);
        if (this.tags.length) {
            this.clear();
            const newWidth = Math.round((this.target.width / dpr) * 1.1);
            this.resize(newWidth);
            this.render();
        }
        this.mount();
    }
}
